import React, { Suspense, lazy, useEffect, useRef, useState } from 'react';

// Lazy-load the heavy IntroVideo component (videos, controls, etc.)
// so it is not part of the initial JS bundle and its CloudFront/Pexels
// requests don't block FCP / LCP / Speed Index on the homepage.
const IntroVideo = lazy(() => import('./IntroVideo'));

const LazyIntroVideo: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [shouldLoad, setShouldLoad] = useState(false);

  useEffect(() => {
    if (shouldLoad) return;
    const el = containerRef.current;
    if (!el) return;

    // If IntersectionObserver isn't available, load immediately as fallback.
    if (typeof IntersectionObserver === 'undefined') {
      setShouldLoad(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setShouldLoad(true);
            observer.disconnect();
            break;
          }
        }
      },
      {
        // Start loading a little before the user reaches the section
        // so the transition feels seamless, but late enough not to hurt LCP.
        rootMargin: '400px 0px',
        threshold: 0.01,
      }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [shouldLoad]);

  return (
    <div ref={containerRef}>
      {shouldLoad ? (
        <Suspense fallback={<IntroVideoPlaceholder />}>
          <IntroVideo />
        </Suspense>
      ) : (
        <IntroVideoPlaceholder />
      )}
    </div>
  );
};

// Lightweight placeholder that reserves the vertical space of the real
// IntroVideo section (prevents layout shift / CLS) but ships zero images.
const IntroVideoPlaceholder: React.FC = () => (
  <section
    aria-hidden="true"
    className="relative bg-[#0D1B2A]"
    style={{ minHeight: '600px' }}
  >
    <div className="max-w-[1440px] mx-auto px-6 lg:px-12 py-20 lg:py-32">
      <div className="text-center mb-16">
        <div className="flex items-center justify-center gap-4 mb-6">
          <div className="w-12 h-[1px] bg-[#C9A961]" />
          <span className="font-sans text-[10px] tracking-[0.4em] uppercase text-[#C9A961]">
            A Cinematic Journey
          </span>
          <div className="w-12 h-[1px] bg-[#C9A961]" />
        </div>
        <h2 className="font-serif text-3xl md:text-5xl text-white font-light mb-4">
          Experience Albania
        </h2>
      </div>
      <div className="relative max-w-5xl mx-auto aspect-video bg-black/60" />
    </div>
  </section>
);

export default LazyIntroVideo;
